/**
 * Auth Store
 *
 * Zustand store for authentication state management.
 * Uses httpOnly cookies for token storage (handled by backend).
 * Only stores user info in client state.
 */

import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import * as authApi from '@/lib/api/auth'
import { ApiError } from '@/types/api-errors'
import type { AuthErrorInfo, AuthState, RegisterData, User } from '@/types/auth'

/** Convert caught error to AuthErrorInfo */
function toAuthError(error: unknown, fallbackMessage: string): AuthErrorInfo {
	if (error instanceof ApiError) {
		return {
			message: error.message,
			code: error.code,
			action: error.action,
		}
	}
	return {
		message: error instanceof Error ? error.message : fallbackMessage,
	}
}

export const useAuthStore = create<AuthState>()(
	persist(
		(set, get) => ({
			user: null,
			isAuthenticated: false,
			isLoading: true, // Start loading to check session on init
			error: null,

			login: async (email: string, password: string) => {
				set({ isLoading: true, error: null })
				try {
					const response = await authApi.login({ email, password })
					set({
						user: response.user,
						isAuthenticated: true,
						isLoading: false,
						error: null,
					})
				} catch (error) {
					set({
						error: toAuthError(error, 'Inloggning misslyckades'),
						isLoading: false,
						isAuthenticated: false,
						user: null,
					})
					throw error
				}
			},

			register: async (data: RegisterData) => {
				set({ isLoading: true, error: null })
				try {
					const response = await authApi.register(data)
					set({
						user: response.user,
						isAuthenticated: true,
						isLoading: false,
						error: null,
					})
				} catch (error) {
					set({
						error: toAuthError(error, 'Registrering misslyckades'),
						isLoading: false,
						isAuthenticated: false,
						user: null,
					})
					throw error
				}
			},

			logout: async () => {
				try {
					await authApi.logout()
				} catch {
					// Ignore logout errors - clear state anyway
				}
				set({
					user: null,
					isAuthenticated: false,
					isLoading: false,
					error: null,
				})
			},

			refreshSession: async () => {
				set({ isLoading: true })
				try {
					const user = await authApi.refreshSession()
					if (user) {
						set({
							user,
							isAuthenticated: true,
							isLoading: false,
							error: null,
						})
					} else {
						set({
							user: null,
							isAuthenticated: false,
							isLoading: false,
							error: null,
						})
					}
				} catch {
					set({
						user: null,
						isAuthenticated: false,
						isLoading: false,
						error: null,
					})
				}
			},

			clearError: () => {
				set({ error: null })
			},

			updateUser: (updates: Partial<User>) => {
				const { user } = get()
				if (user) {
					set({ user: { ...user, ...updates } })
				}
			},

			setUser: (user: User | null) => {
				set({
					user,
					isAuthenticated: user !== null,
					isLoading: false,
				})
			},
		}),
		{
			name: 'auth-storage',
			storage: createJSONStorage(() => localStorage),
			// Only persist user info, not loading/error state
			partialize: state => ({
				user: state.user,
				isAuthenticated: state.isAuthenticated,
			}),
			// On rehydrate, check for token and refresh session
			onRehydrateStorage: () => state => {
				// Check if we have a token - if so, always refresh session
				// This handles the case where token exists but zustand state was cleared
				const token = localStorage.getItem('auth-token')
				if (token) {
					state?.refreshSession()
				} else {
					state?.setUser(null)
				}
			},
		},
	),
)

/**
 * Hook to check if user is authenticated
 */
export function useIsAuthenticated(): boolean {
	return useAuthStore(state => state.isAuthenticated)
}

/**
 * Hook to get current user
 */
export function useCurrentUser(): User | null {
	return useAuthStore(state => state.user)
}

/**
 * Hook to check if auth is loading
 */
export function useAuthLoading(): boolean {
	return useAuthStore(state => state.isLoading)
}
